// src/Layout/Header/Breadcrumb.jsx
import React from "react";
import { useLocation } from "react-router-dom";
import IConOne    from "../../assest/svg/Executive.svg";
import IconTwo    from "../../assest/svg/Geospatial.svg";
import IconThree  from "../../assest/svg/LivestockGroup.svg";
import IconFour   from "../../assest/svg/Market.svg";
import IconFive   from "../../assest/svg/Infrastructure.svg";
import IconSix    from "../../assest/svg/Workforce.svg";
import IconSeven  from "../../assest/svg/Planning.svg";
import IconEight  from "../../assest/svg/Conflict.svg";
import IconNine   from "../../assest/svg/AnimalIdentity.svg";
import IconTen    from "../../assest/svg/AnimalHealth.svg";
import IconEleven from "../../assest/svg/TrustCenter.svg";
import IconTwelve from "../../assest/svg/RegistryManagement.svg";

// ── Module-level route config ─────────────────────────────────────────────────
// base path → { category, page label, icon }
const MODULE_MAP = {
  "executive-overview":      { category: "Dashboard",          page: "Executive Overview",                icon: IConOne    },
  "geospatial-overview":     { category: "Dashboard",          page: "Geospatial Overview",               icon: IconTwo    },
  "livestock-population":    { category: "Core Modules",       page: "Livestock Population", icon: IconThree  },
  "markets-trade":           { category: "Core Modules",       page: "Markets & Trade Intelligence",      icon: IconFour   },
  "infrastructure":          { category: "Core Modules",       page: "Infrastructure & Services",         icon: IconFive   },
  "workforce-institutional": { category: "Core Modules",       page: "Workforce & Institutional Actors",  icon: IconSix    },
  "planning-reporting":      { category: "Core Modules",       page: "Planning & Reporting (PAR)",        icon: IconSeven  },
  "conflicts-dispute":       { category: "Core Modules",       page: "Conflicts & Dispute Records",       icon: IconEight  },
  "animal-identification":   { category: "Integrated Modules", page: "Animal Identification",             icon: IconNine   },
  "animal-health":           { category: "Integrated Modules", page: "Animal Health",                     icon: IconTen    },
  "admin":               { category: "System Management", page: "Admin & Security",    icon: IconEleven },
  "registry-management": { category: "System Management", page: "Registry Management", icon: IconTwelve },
};

// ── Third-level sub-tab labels (module → tab → sub-tab) ─────────────────────
const SUBTAB_LABEL_MAP = {
  "admin": {
    "users": {
      "list":        "User",
      "assignments": "User Role Assignments",
    },
  },
  "registry-management": {
    "species": {
      "species-registry": "Species Registry",
      "breed":            "Breed Ecotype Registry",
      "purpose":          "Production Purpose Registry",
      "age":              "Age Class Registry",
    },
  },
};

// ── Sub-route (tab) segment → human-readable label ────────────────────────────
// Keyed by module base path so the same tab slug (e.g. "validation") can mean
// different things in different modules without collision.
const TAB_LABEL_MAP = {
  "livestock-population": {
    "livestock-population-record": "Livestock Population Records",
    "new":                         "Add New Record",
    "edit":                        "Edit Record",
    "view":                        "Record Details",
  },
  "markets-trade": {
    "market-trade":                      "Market Registry",
    "market-prices":                     "Market Prices",
    "border-crossing-registry":          "Border Crossing Registry",
    "veterinary-certificate-registry":   "Veterinary Certificate Registry",
    "new":                               "Add New Record",
  },
  "infrastructure": {
    "core":       "Facility Core Registry",
    "services":   "Facility Services",
    "catchment":  "Facility Catchment Areas",
    "validation": "Facility Validation History",
    "inspection": "Facility Inspection Registry",
    "feed":       "Feed Production",
    "new":        "Add New Record",
    "edit":       "Edit Record",
    "view":       "Record Details",
  },
  "workforce-institutional": {
    "actors":     "Livestock Actors",
    "deployment": "Workforce Deployment Records",
    "coop":       "Cooperatives & Organization",
    "insurance":  "Insurance Records – NAIC",
    "finance":    "Finance Access Records – CBN/NIRSAL/BOA",
  },
  "planning-reporting": {
    "workplans":    "Workplan & Activities",
    "reports":      "Activity Reports",
    "publications": "Publication Registry",
    "validation":   "PAR Validation History",
  },
  "conflicts-dispute": {
    "records":    "Conflict Records",
    "committees": "Conflict Committees",
    "routes":     "Transhumance Routes",
    "training":   "CMC Training Records",
    "mediation":  "Mediation & Resolution Records",
  },
  "animal-health": {
    "outbreak":    "Outbreak Records",
    "vaccination": "Vaccination Records",
    "mortality":   "Mortality Records",
    "zoonotic":    "Zoonotic & AMR Flags",
  },
  "admin": {
    "institutions":  "Institution Management",
    "users":         "User Management",
    "geospatial":    "Location Management",
    "audit":         "Audit Log",
    "supplementary": "Supplementary Statistical Datasets",
  },
  "registry-management": {
    "species":       "Species & Livestock Registry",
    "departments":   "FMLD Department Registry",
    "forms":         "Form Registry",
    "ppp":           "PPP Arrangement Registry",
    "central":       "Registry Central",
  },
};

const GREEN_FILTER =
  "invert(40%) sepia(90%) saturate(400%) hue-rotate(90deg) brightness(90%)";

const Sep = () => (
  <span style={{ color: "#D1D5DB", fontWeight: 400, margin: "0 2px" }}>/</span>
);

// ── Breadcrumb reads everything it needs from the URL itself ──────────────────
const Breadcrumb = () => {
  const { pathname } = useLocation();

  const segments   = pathname.split("/").filter(Boolean);
  const baseKey    = segments[0] ?? "";
  const tabKey     = segments[1] ?? "";
  const subTabKey  = segments[2] ?? "";

  const meta        = MODULE_MAP[baseKey] ?? { category: "Dashboard", page: "Overview", icon: null };
  const tabLabel    = TAB_LABEL_MAP[baseKey]?.[tabKey] ?? null;
  const subTabLabel = SUBTAB_LABEL_MAP[baseKey]?.[tabKey]?.[subTabKey] ?? null;

  const hasTab    = Boolean(tabLabel);
  const hasSubTab = Boolean(subTabLabel);

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 6,
      fontSize: 14, fontWeight: 500, userSelect: "none",
    }}>
      {/* Category */}
      <span style={{ color: "#1F2937", fontWeight: 600 }}>
        {meta.category}
      </span>

      <Sep />

      {/* Module page — grey when a sub-tab is active, green when it's the leaf */}
      <span style={{
        display: "flex", alignItems: "center", gap: 6,
        fontWeight: 700,
        color: hasTab ? "#374151" : "#16A34A",
      }}>
        {meta.icon && (
          <img
            src={meta.icon}
            alt=""
            style={{
              width: 15, height: 15,
              filter: hasTab ? undefined : GREEN_FILTER,
            }}
          />
        )}
        {meta.page}
      </span>

      {/* Tab label — grey when a sub-sub-tab is active, green when it's the leaf */}
      {hasTab && (
        <>
          <Sep />
          <span style={{ color: hasSubTab ? "#374151" : "#16A34A", fontWeight: 700 }}>
            {tabLabel}
          </span>
        </>
      )}

      {/* Sub-tab label — always green, only when a third URL segment is active */}
      {hasSubTab && (
        <>
          <Sep />
          <span style={{ color: "#16A34A", fontWeight: 700 }}>{subTabLabel}</span>
        </>
      )}
    </div>
  );
};

export default Breadcrumb;
